// BL-2338 — one per-item edit of a lot's media or documents array: add one item
// by its client-made id, or remove one. Pure, so the server runs it under the lot
// lock and the offline queue (BL-2345) can run the same edit on its cached lot.
// BL-2348 — the online drawer's change list folds through the same edits, plus
// one media order step, and the drawer builds that list here from its arrays.

import {
  MAX_EXTERNAL_DOCS_PER_LOT,
  MAX_MEDIA_PER_LOT,
  mediaSignature,
  normalizeMediaForWrite,
  withFirstAsMain,
} from '@auctogon/core/inventory';
import type { ExternalDocument } from '@auctogon/db/types/jsonb';

/** The fields the media edits read: a stored record and the drawer's in-memory one both have them. */
type MediaLike = { id: string; isCover: boolean; sortOrder: number };

/** One per-item change: add an item (keyed by its own `id`), or remove one by id. */
export type LotAttachmentEdit<T> = { op: 'add'; item: T } | { op: 'remove'; id: string };

/**
 * The edit's outcome. `changed: false` is an idempotent no-op — the item was
 * already there (add) or already gone (remove) — so the caller writes nothing.
 * `refused: 'cap'` is an add on a lot that already holds the maximum.
 */
export type LotAttachmentEditOutcome<T> =
  | { changed: false }
  | { changed: true; next: T[] }
  | { refused: 'cap' };

/**
 * Apply one edit to an attachment array, keyed by `id`.
 *
 * @param current - the array as stored.
 * @param edit - the add or remove.
 * @param opts.max - the per-lot cap an add may not pass.
 * @param opts.normalize - re-establishes the array's invariants after a change.
 * @returns the outcome; `next` is a new array.
 */
function applyAttachmentEdit<T extends { id: string }>(
  current: readonly T[],
  edit: LotAttachmentEdit<T>,
  opts: { max: number; normalize: (items: T[]) => T[] },
): LotAttachmentEditOutcome<T> {
  if (edit.op === 'remove') {
    if (!current.some((entry) => entry.id === edit.id)) return { changed: false };
    return { changed: true, next: opts.normalize(current.filter((entry) => entry.id !== edit.id)) };
  }
  if (current.some((entry) => entry.id === edit.item.id)) return { changed: false };
  if (current.length >= opts.max) return { refused: 'cap' };
  return { changed: true, next: opts.normalize([...current, edit.item]) };
}

/**
 * Add or remove one media record (BL-2338). An added record goes last and never takes the cover from the
 * one that has it; removing the cover makes the first remaining record the cover (`normalizeMediaForWrite`,
 * the photo manager's rule). BL-2816 deleted the per-item routes that ran this beside the order edit and
 * its stale-base check; the lot Save's change list still folds each add and remove through it.
 *
 * @param current - the lot's stored media array.
 * @param edit - the add or remove.
 * @returns the outcome; `next` is normalized (gapless `sortOrder`, one cover).
 */
export function applyLotMediaEdit<T extends MediaLike>(
  current: readonly T[],
  edit: LotAttachmentEdit<T>,
): LotAttachmentEditOutcome<T> {
  // An added record's own cover flag never wins: the existing cover is first
  // in the array, and `normalizeMediaForWrite` keeps the first one it finds.
  const keyed = edit.op === 'add' ? { op: 'add' as const, item: { ...edit.item, isCover: false } } : edit;
  return applyAttachmentEdit(current, keyed, { max: MAX_MEDIA_PER_LOT, normalize: (items) => normalizeMediaForWrite(items) });
}

/**
 * BL-2676 (ADR 070 — Ingest Protocol § 5) — place one ingested photo or video.
 * `position` is the caller's integer slot. The item lands after every stored item whose
 * `sortOrder` is at or below it, so an added item sorts after a stored one at an equal
 * position and never displaces it. A cover request then moves the item to index 0, and the
 * array is renormalized (gapless `sortOrder`, the first item the one cover). A request
 * without the cover flag never takes the cover. Last applied wins by arrival order, which
 * the caller guarantees by running this under the lot lock.
 *
 * @param current - the lot's stored media array.
 * @param input.item - the media record to place (its `id` is the client media id).
 * @param input.position - the requested integer slot, 0 or more.
 * @param input.cover - whether this item asks to be the cover.
 * @returns `changed: false` when the id is already present (a repeat), `refused: 'cap'` at the per-lot cap.
 */
export function applyLotMediaIngest<T extends MediaLike>(
  current: readonly T[],
  input: { item: T; position: number; cover: boolean },
): LotAttachmentEditOutcome<T> {
  if (current.some((entry) => entry.id === input.item.id)) return { changed: false };
  if (current.length >= MAX_MEDIA_PER_LOT) return { refused: 'cap' };
  const at = current.filter((entry) => entry.sortOrder <= input.position).length;
  const placed = [...current.slice(0, at), { ...input.item, isCover: false }, ...current.slice(at)];
  if (!input.cover) {
    const keptCover = current.find((entry) => entry.isCover);
    // The stored cover keeps the flag; `normalizeMediaForWrite` honors the first flagged item.
    return { changed: true, next: normalizeMediaForWrite(placed.map((entry) => ({ ...entry, isCover: keptCover?.id === entry.id }))) };
  }
  const moved = [{ ...input.item, isCover: true }, ...placed.filter((entry) => entry.id !== input.item.id)];
  return { changed: true, next: withFirstAsMain(moved) };
}

/**
 * Add or remove one document. An added document goes last; order is otherwise kept.
 *
 * @param current - the lot's stored documents array.
 * @param edit - the add or remove.
 * @returns the outcome.
 */
export function applyLotDocumentEdit(
  current: readonly ExternalDocument[],
  edit: LotAttachmentEdit<ExternalDocument>,
): LotAttachmentEditOutcome<ExternalDocument> {
  return applyAttachmentEdit(current, edit, { max: MAX_EXTERNAL_DOCS_PER_LOT, normalize: (items) => items });
}

/**
 * BL-2348 — put a lot's media in the given order. Main is the first item
 * afterwards (`withFirstAsMain`, BL-1979's rule). It names records by id only,
 * so it can reorder what is there but never add, drop or replace a record: an
 * item the order does not name (one another device added) keeps its relative
 * place after the named ones, and an id the lot no longer carries is ignored.
 *
 * @param current - the lot's stored media array.
 * @param ids - the ids in their new order.
 * @returns the outcome; `changed: false` when order and Main already match.
 */
export function applyLotMediaOrder<T extends MediaLike>(
  current: readonly T[],
  ids: readonly string[],
): { changed: false } | { changed: true; next: T[] } {
  const byId = new Map(current.map((item) => [item.id, item]));
  const named = new Set<string>();
  const ordered: T[] = [];
  for (const id of ids) {
    const item = byId.get(id);
    if (item && !named.has(id)) {
      named.add(id);
      ordered.push(item);
    }
  }
  const next = withFirstAsMain([...ordered, ...current.filter((item) => !named.has(item.id))]);
  return mediaSignature(next) === mediaSignature(current) ? { changed: false } : { changed: true, next };
}

/**
 * BL-2677 (ADR 070 — Ingest Protocol § 5) — put a lot's media in the order a map of new
 * positions gives. An item's effective position is `positions[id] ?? item.sortOrder`; items sort by
 * (position, id), a total order, and the id list goes through `applyLotMediaOrder`, so the first
 * item becomes Main and an unchanged result reports no change. Ids in `positions` that the lot
 * lacks are ignored. There is no base-order check in this lane.
 *
 * @param current - the lot's stored media array.
 * @param positions - new position by media id.
 * @returns the outcome; `changed: false` when order and Main already match.
 */
export function applyLotMediaPositions<T extends MediaLike>(
  current: readonly T[],
  positions: Readonly<Record<string, number>>,
): { changed: false } | { changed: true; next: T[] } {
  const own = (id: string): number | undefined => (Object.hasOwn(positions, id) ? positions[id] : undefined);
  const placed = current.map((item) => ({ id: item.id, at: own(item.id) ?? item.sortOrder }));
  placed.sort((x, y) => x.at - y.at || (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
  return applyLotMediaOrder(current, placed.map((entry) => entry.id));
}

/** BL-2348 — a Save's per-item media changes: removes, then adds, then an optional order. */
export interface LotMediaChanges<T> {
  add: T[];
  remove: string[];
  order?: string[];
  /**
   * BL-2677 — new positions by media id, applied after the removes. An id the lot lacks, or
   * removed in the same request, is ignored; an unnamed item keeps its `sortOrder` as its position.
   */
  positions?: Record<string, number>;
}

/** BL-2348 — a Save's per-item document changes. */
export interface LotDocumentChanges<T> {
  add: T[];
  remove: string[];
}

/**
 * Fold a list of per-item edits, stopping at the first refusal.
 *
 * @returns the outcome; `changed` is true when any edit changed the array.
 */
function foldEdits<T>(
  current: readonly T[],
  steps: Array<(items: readonly T[]) => LotAttachmentEditOutcome<T>>,
): LotAttachmentEditOutcome<T> {
  let items = current;
  let changed = false;
  for (const step of steps) {
    const outcome = step(items);
    if ('refused' in outcome) return outcome;
    if (outcome.changed) {
      items = outcome.next;
      changed = true;
    }
  }
  return changed ? { changed: true, next: [...items] } : { changed: false };
}

/**
 * BL-2348 — apply a Save's media change list as per-item edits: each remove,
 * then BL-2677's positions, then each add (BL-2338's `applyLotMediaEdit`), then the order. Applied to the
 * array as stored, so an item another device added survives.
 *
 * @param current - the lot's stored media array.
 * @param changes - the change list.
 * @returns the outcome; `refused: 'cap'` when an add would pass the per-lot cap.
 */
export function applyLotMediaChanges<T extends MediaLike>(
  current: readonly T[],
  changes: LotMediaChanges<T>,
): LotAttachmentEditOutcome<T> {
  const { order, positions } = changes;
  return foldEdits<T>(current, [
    ...changes.remove.map((id) => (items: readonly T[]) => applyLotMediaEdit(items, { op: 'remove', id })),
    ...(positions ? [(items: readonly T[]) => applyLotMediaPositions(items, positions)] : []),
    ...changes.add.map((item) => (items: readonly T[]) => applyLotMediaEdit(items, { op: 'add', item })),
    ...(order ? [(items: readonly T[]) => applyLotMediaOrder(items, order)] : []),
  ]);
}

/**
 * BL-2348 — apply a Save's document change list: each remove, then each add.
 *
 * @param current - the lot's stored documents array.
 * @param changes - the change list.
 * @returns the outcome; `refused: 'cap'` when an add would pass the per-lot cap.
 */
export function applyLotDocumentChanges<T extends { id: string }>(
  current: readonly T[],
  changes: LotDocumentChanges<T>,
): LotAttachmentEditOutcome<T> {
  return foldEdits<T>(current, [
    ...changes.remove.map((id) => (items: readonly T[]) =>
      applyAttachmentEdit(items, { op: 'remove', id }, { max: MAX_EXTERNAL_DOCS_PER_LOT, normalize: (next) => next })),
    ...changes.add.map((item) => (items: readonly T[]) =>
      applyAttachmentEdit(items, { op: 'add', item }, { max: MAX_EXTERNAL_DOCS_PER_LOT, normalize: (next) => next })),
  ]);
}

/** Ids in `from` that `to` lacks. */
function idsMissingFrom(from: readonly { id: string }[], to: readonly { id: string }[]): string[] {
  const kept = new Set(to.map((item) => item.id));
  return from.filter((item) => !kept.has(item.id)).map((item) => item.id);
}

/** Items in `to` whose id `from` lacks. */
function itemsNewIn<T extends { id: string }>(from: readonly { id: string }[], to: readonly T[]): T[] {
  const had = new Set(from.map((item) => item.id));
  return to.filter((item) => !had.has(item.id));
}

/**
 * BL-2348 — the lot drawer's change list: what Save sends instead of whole
 * arrays. A media `order` is added only when the drawer's final sequence or Main
 * differs from what its adds and removes alone would give, judged by running the
 * same fold the server runs over the seed. Empty lists are omitted.
 *
 * @param input.seededMedia - the media the drawer opened with.
 * @param input.media - the drawer's media at Save.
 * @param input.seededDocs - the documents the drawer opened with.
 * @param input.docs - the drawer's documents at Save.
 * @returns `{ mediaChanges?, documentChanges? }`.
 */
export function buildLotAttachmentChanges<M extends MediaLike, D extends { id: string }>(input: {
  seededMedia: readonly M[];
  media: readonly M[];
  seededDocs: readonly D[];
  docs: readonly D[];
}): { mediaChanges?: LotMediaChanges<M>; documentChanges?: LotDocumentChanges<D> } {
  const result: { mediaChanges?: LotMediaChanges<M>; documentChanges?: LotDocumentChanges<D> } = {};

  const media: LotMediaChanges<M> = {
    add: itemsNewIn(input.seededMedia, input.media),
    remove: idsMissingFrom(input.seededMedia, input.media),
  };
  const folded = applyLotMediaChanges(input.seededMedia, media);
  const predicted = 'next' in folded ? folded.next : input.seededMedia;
  if (mediaSignature(predicted) !== mediaSignature(input.media)) media.order = input.media.map((item) => item.id);
  if (media.add.length > 0 || media.remove.length > 0 || media.order) result.mediaChanges = media;

  const documents: LotDocumentChanges<D> = {
    add: itemsNewIn(input.seededDocs, input.docs),
    remove: idsMissingFrom(input.seededDocs, input.docs),
  };
  if (documents.add.length > 0 || documents.remove.length > 0) result.documentChanges = documents;

  return result;
}
